'use client'

/**
 * RippleCard Component
 * 
 * A card component with a subtle ripple background effect on hover.
 */

import { useState, useCallback } from 'react'

const VARIANTS = {
  default: {
    background: '#FFFFFF',
    border: 'none',
    shadow: '0 4px 6px -1px rgb(0 0 0 / 0.07), 0 2px 4px -2px rgb(0 0 0 / 0.07)',
    hoverShadow: '0 20px 25px -5px rgb(0 0 0 / 0.08), 0 8px 10px -6px rgb(0 0 0 / 0.08)',
    rippleColor: 'rgba(204, 251, 241, 0.8)'
  },
  elevated: {
    background: '#FFFFFF',
    border: 'none',
    shadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    hoverShadow: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
    rippleColor: 'rgba(153, 246, 228, 0.6)'
  },
  outlined: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    shadow: 'none',
    hoverShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
    rippleColor: 'rgba(240, 253, 250, 0.9)'
  },
  gradient: {
    background: 'linear-gradient(135deg, #F0FDFA 0%, #FFFFFF 100%)',
    border: '1px solid rgba(20, 184, 166, 0.1)',
    shadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
    hoverShadow: '0 20px 25px -5px rgba(13, 148, 136, 0.1)',
    rippleColor: 'rgba(94, 234, 212, 0.4)'
  }
}

const PADDING = {
  none: '0',
  sm: '16px',
  md: '24px',
  lg: '32px'
}

export default function RippleCard({
  children,
  variant = 'default',
  padding = 'md',
  borderRadius = '16px',
  header = null,
  footer = null,
  onClick = null,
  className = '',
  as: Component = 'div',
  ...props
}) {
  const [isHovered, setIsHovered] = useState(false)
  const variantStyles = VARIANTS[variant] || VARIANTS.default
  const paddingValue = PADDING[padding] || padding
  const isClickable = Boolean(onClick)

  const handleKeyDown = useCallback((e) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onClick(e)
    }
  }, [isClickable, onClick])

  const cardStyle = {
    position: 'relative',
    overflow: 'hidden',
    background: variantStyles.background,
    border: variantStyles.border,
    borderRadius: borderRadius,
    boxShadow: isHovered ? variantStyles.hoverShadow : variantStyles.shadow,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
    cursor: isClickable ? 'pointer' : 'default'
  }

  const rippleStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: isHovered ? '150%' : '0%',
    height: isHovered ? '150%' : '0%',
    background: `radial-gradient(circle at center, ${variantStyles.rippleColor} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.5s ease-out, height 0.5s ease-out',
    pointerEvents: 'none',
    zIndex: 0
  }

  return (
    <Component
      className={`hf-ripple-card ${className}`}
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      {...props}
    >
      <div style={rippleStyle} aria-hidden="true" />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        {header && (
          <div style={{ padding: paddingValue, paddingBottom: `calc(${paddingValue} / 2)`, borderBottom: '1px solid #F3F4F6' }}>
            {header}
          </div>
        )}
        <div style={{ padding: paddingValue }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: paddingValue, paddingTop: `calc(${paddingValue} / 2)`, borderTop: '1px solid #F3F4F6' }}>
            {footer}
          </div>
        )}
      </div>
    </Component>
  )
}
